import {
    Avatar,
    Box,
    Container,
    FormControl,
    MenuItem,
    Select,
    Typography,
    ToggleButtonGroup,
    ToggleButton,
    IconButton,
    InputLabel,
    ListItemText,
    OutlinedInput,
    Checkbox,
    Switch,
    FormGroup,
    FormControlLabel,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import HomeAppBar from "../../components/HomeAppBar/HomeAppBar";
import MuiTable from "../../components/MuiTable/MuiTable";
import {
    buildTableDateFormatter,
    formatDateTime,
    formatStepCount,
    getNumberSign,
    hasAccess,
    httpRequest,
    onRequestError,
    printLog,
} from "../../helper/global";
import {
    FIRST_DATA_LIST,
    HEALTH_LABEL,
    HEALTH_TYPE,
    LOCAL_STORAGE_KEY,
    MEMBER_LIST,
    SUBSCRIBED_LIST,
    PERMISSIONS,
    PLATFORM_LIST,
    STATUS,
    STATUS_LIST,
} from "../../helper/constant";
import { useDispatch } from "react-redux";
import { showAlert } from "../../reducers/alertSlice";
import { hideLoader, showLoader } from "../../reducers/loaderSlice";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import { clearPatient } from "../../reducers/patientSlice";
import Email from "../../components/Email/Email";

export default function Patients({ theme, user, permissionConfig }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const appBarRef = useRef(null);

    const containerStyle = { ...theme.container };
    containerStyle.p = "0px !important";
    containerStyle.m = "0px !important";

    const [ready, setReady] = useState(false);

    const [updatedRows, setUpdatedRows] = useState([]);

    const [appVersions, setAppVersions] = useState([]);

    const [selectedAppVersion, setSelectedAppVersion] = useState([]);

    const [statusToggle, setStatusToggle] = useState([STATUS_LIST[0].value]);

    const [memberToggle, setMemberToggle] = useState([MEMBER_LIST[0].value]);

    //both options selected by default, so the list is unchanged until staff
    //deliberately narrows it
    const [subscribedToggle, setSubscribedToggle] = useState([
        SUBSCRIBED_LIST[0].value,
        SUBSCRIBED_LIST[1].value,
    ]);

    const [platformToggle, setPlatformToggle] = useState([]);

    const [firstData, setFirstData] = useState([
        FIRST_DATA_LIST[0].value,
        FIRST_DATA_LIST[1].value,
    ]);

    const [openEmail, setOpenEmail] = useState(false);

    const [initialRecipient, setInitialRecipient] = useState([]);

    const [extraParams, setExtraParams] = useState({
        member: memberToggle,
        subscribed: subscribedToggle,
        status: statusToggle,
        platform: platformToggle,
        buildVersion: selectedAppVersion,
    });

    const trendKey = { key: "14d", title: "Last 14 days" };

    const getAppVersions = async () => {
        try {
            const result = await httpRequest(
                `patient/appVersions`,
                {},
                "GET",
                (error) => {
                    onRequestError(dispatch, navigate, {
                        title: error.title,
                        message: error.message,
                    });
                },
            );

            if (result === false) {
                return;
            }

            setAppVersions(result.payload ?? []);
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    const initFilterValues = (cache) => {
        setSelectedAppVersion(
            cache && cache.extraParams && cache.extraParams.buildVersion
                ? cache.extraParams.buildVersion
                : [],
        );

        setStatusToggle(
            cache && cache.extraParams && cache.extraParams.status
                ? cache.extraParams.status
                : [STATUS_LIST[0].value],
        );

        setPlatformToggle(
            cache && cache.extraParams && cache.extraParams.platform
                ? cache.extraParams.platform
                : [],
        );

        setMemberToggle(
            cache && cache.extraParams && cache.extraParams.member
                ? cache.extraParams.member
                : [MEMBER_LIST[0].value],
        );

        setSubscribedToggle(
            cache && cache.extraParams && cache.extraParams.subscribed
                ? cache.extraParams.subscribed
                : [SUBSCRIBED_LIST[0].value, SUBSCRIBED_LIST[1].value],
        );

        setFirstData(
            cache && cache.extraParams && cache.extraParams.firstData
                ? cache.extraParams.firstData
                : [FIRST_DATA_LIST[0].value, FIRST_DATA_LIST[1].value],
        );
    };

    useEffect(() => {
        dispatch(clearPatient());

        setTimeout(() => {
            setReady(true);
        }, 200);

        getAppVersions();
    }, []);

    useEffect(() => {
        const copy = { ...extraParams };

        copy.status = statusToggle;
        copy.platform = platformToggle;
        copy.buildVersion = selectedAppVersion;
        copy.member = memberToggle;
        copy.subscribed = subscribedToggle;
        copy.firstData = firstData;

        setExtraParams(copy);
    }, [
        memberToggle,
        subscribedToggle,
        statusToggle,
        platformToggle,
        selectedAppVersion,
        firstData,
    ]);

    const handleStatusChange = async (row, status) => {
        row.status = status;
        setUpdatedRows([row]);
        try {
            const result = await httpRequest(
                `patient/update/${row.id}`,
                { status: status },
                "POST",
                (error) => {
                    onRequestError(dispatch, navigate, {
                        title: error.title,
                        message: error.message,
                    });
                },
            );

            if (result === false) {
                return;
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    const handleSubscribedChange = async (row, subscribed) => {
        //optimistic, matching handleMemberChange
        row.optOutEmail = !subscribed;
        setUpdatedRows([row]);

        try {
            const result = await httpRequest(
                `patient/emailSubscription/${row.id}`,
                { subscribed },
                "POST",
                (error) => {
                    onRequestError(dispatch, navigate, {
                        title: error.title,
                        message: error.message,
                    });
                },
            );

            if (result === false) {
                //put the switch back if the write failed
                row.optOutEmail = subscribed;
                setUpdatedRows([row]);
                return;
            }
        } catch (error) {
            console.error("Error updating subscription:", error);
        }
    };

    const handleMemberChange = async (row, member) => {
        row.member = member ? 1 : 0;
        setUpdatedRows([row]);

        try {
            const result = await httpRequest(
                `patient/update/${row.id}`,
                { member: member ? 1 : 0 },
                "POST",
                (error) => {
                    onRequestError(dispatch, navigate, {
                        title: error.title,
                        message: error.message,
                    });
                },
            );

            if (result === false) {
                return;
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    const handleOnPatientDelete = (row) => {
        dispatch(
            showAlert({
                title: "Confirm",
                message: `Do you want to deleted ${row.name}?`,
                buttons: [
                    {
                        label: "Delete",
                        callback: async () => {
                            const users = [row.id];
                            try {
                                dispatch(
                                    showLoader({
                                        message:
                                            "Deleting selected patients...",
                                    }),
                                );
                                const result = await httpRequest(
                                    `patient/delete`,
                                    { users: users },
                                    "DELETE",
                                    (error) => {
                                        dispatch(hideLoader());
                                        onRequestError(dispatch, navigate, {
                                            title: error.title,
                                            message: error.message,
                                        });
                                    },
                                );
                                dispatch(hideLoader());
                                if (result === false) {
                                    return;
                                }
                                window.location.reload();
                            } catch (error) {
                                console.error("Error fetching data:", error);
                            }
                        },
                    },
                    { label: "Cancel" },
                ],
            }),
        );
    };

    const formatHealthData = (value, row, index, key) => {
        if (!value) {
            return "-";
        }

        let trendIcon = "";
        if (
            row.healthTrends &&
            row.healthTrends[key] &&
            row.healthTrends[key][trendKey.key]
        ) {
            const trend = row.healthTrends[key][trendKey.key].direction;

            trendIcon = trend === "up" ? " ▲" : trend === "down" ? " ▼" : " -";
        }

        return `${Number(value).toLocaleString()}${trendIcon}`;
    };

    const formatHealthDataTooltip = (value, row, index, key) => {
        if (!value) {
            return "-";
        }

        if (
            row.healthTrends &&
            row.healthTrends[key] &&
            row.healthTrends[key][trendKey.key]
        ) {
            return `${HEALTH_LABEL[key]}: ${trendKey.title} trend`;
        }

        return `No Trend Data`;
    };

    const columns = [
        {
            id: "name",
            label: "Name",
            sort: true,
            visible: true,
            checkbox: true,
            hasAvatar: true,
            formatter: (value, row, index) => {
                let email = row.emailAddress ?? "";

                if (
                    row.signInService === "Email" &&
                    !row.lastLogin &&
                    !row.adminVerified
                ) {
                    email += " (unverified)";
                }

                return (
                    <Box display={"flex"} alignItems={"center"}>
                        <Avatar
                            alt={value}
                            src={row.avatar ?? value}
                            sx={{ mr: 1 }}
                        />
                        <Box>
                            <Typography>{value}</Typography>
                            <Typography
                                variant="body2"
                                sx={{ color: theme.palette.text.secondary }}
                            >
                                {email}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            id: HEALTH_TYPE.BODY_MASS,
            label: `${HEALTH_LABEL[HEALTH_TYPE.BODY_MASS]} (lbs)`,
            sort: true,
            visible: true,
            tooltip: formatHealthDataTooltip,
            formatter: formatHealthData,
        },
        {
            id: HEALTH_TYPE.BMI,
            label: HEALTH_LABEL[HEALTH_TYPE.BMI],
            sort: true,
            visible: true,
            sx: { width: "100px" },
            tooltip: formatHealthDataTooltip,
            formatter: formatHealthData,
        },
        {
            id: HEALTH_TYPE.BODY_FAT_PERCENTAGE,
            label: HEALTH_LABEL[HEALTH_TYPE.BODY_FAT_PERCENTAGE],
            sort: true,
            visible: true,
            tooltip: formatHealthDataTooltip,
            formatter: formatHealthData,
        },
        {
            id: HEALTH_TYPE.LEAN_BODY_MASS,
            label: HEALTH_LABEL[HEALTH_TYPE.LEAN_BODY_MASS],
            sort: true,
            visible: true,
            tooltip: formatHealthDataTooltip,
            formatter: formatHealthData,
        },
        {
            id: HEALTH_TYPE.STEP_COUNT,
            label: HEALTH_LABEL[HEALTH_TYPE.STEP_COUNT],
            sort: true,
            visible: true,
            formatter: formatStepCount,
        },
        {
            id: "change",
            label: "Change (lbs)",
            sort: true,
            visible: true,
            formatter: (value, row, index) => {
                if (!value) {
                    return "-";
                }

                return value;

                let html = [
                    <Box key={`change-${row.id}`} sx={{ display: "flex" }}>
                        <Typography
                            variant="body"
                            sx={{ whiteSpace: "nowrap" }}
                        >
                            Weight: {value}
                        </Typography>
                    </Box>,
                ];

                if (row.changeLeanBodyMass) {
                    html.push(
                        <Box
                            key={`changeLeanBodyMass-${row.id}`}
                            sx={{ display: "flex" }}
                        >
                            <Typography
                                variant="body"
                                sx={{ whiteSpace: "nowrap" }}
                            >
                                Lean Mass:{" "}
                                {getNumberSign(row.changeLeanBodyMass)}
                                {row.changeLeanBodyMass.toFixed(2)}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: theme.palette.text.secondary,
                                    ml: 1,
                                }}
                            >
                                (
                                {(
                                    Math.abs(row.changeLeanBodyMass / value) *
                                    100
                                ).toFixed(0)}
                                %)
                            </Typography>
                        </Box>,
                    );
                }

                if (row.changeBodyFatPercentage) {
                    html.push(
                        <Box
                            key={`changeBodyFatPercentage-${row.id}`}
                            sx={{ display: "flex" }}
                        >
                            <Typography
                                variant="body"
                                sx={{ whiteSpace: "nowrap" }}
                            >
                                Body Fat:{" "}
                                {getNumberSign(row.changeBodyFatPercentage)}
                                {row.changeBodyFatPercentage.toFixed(2)}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: theme.palette.text.secondary,
                                    ml: 1,
                                }}
                            >
                                (
                                {(
                                    Math.abs(
                                        row.changeBodyFatPercentage / value,
                                    ) * 100
                                ).toFixed(0)}
                                %)
                            </Typography>
                        </Box>,
                    );
                }

                return <Box>{html}</Box>;
            },
        },
        {
            id: "bodyMassDate",
            label: "Last Weigh-In",
            sort: true,
            visible: true,
            formatter: (value, row, index) => {
                return buildTableDateFormatter(theme, value);
            },
        },
        {
            id: "daysFromFirstData",
            label: "Days",
            headerTooltip: "# days from Start Date to First Data",
            sort: true,
            visible: false,
            formatter: (value, row, index) => {
                if (!value) {
                    return "-";
                }

                return Math.abs(value) - 1;
            },
            tooltip: (value, row, index) => {
                if (!value) {
                    return "No Start Date or First Data";
                }

                // return `${Math.abs(value)} days from Start Date to First Data`;
                return (
                    <Box>
                        <Typography variant="body2">
                            {`${Math.abs(value) - 1} day(s) from Start Date to First Data`}
                        </Typography>
                        <Typography variant="body2">
                            {`Start Date: ${formatDateTime(row.startingWeightDate ?? null, "MM/DD/YYYY", false)}`}
                        </Typography>
                        <Typography variant="body2">
                            {`First Data Date: ${formatDateTime(row.firstEntryData && row.firstEntryData.length > 0 ? row.firstEntryData[0].date : null, "MM/DD/YYYY", false)}`}
                        </Typography>
                    </Box>
                );
            },
        },
        {
            id: "platformVersion",
            label: "Platform",
            sort: true,
            visible: false,
        },

        {
            id: "signInService",
            label: "Sign In Service",
            sort: true,
            visible: false,
        },
        {
            id: "accountCreatedOn",
            label: "Created On",
            sort: true,
            visible: true,
            formatter: (value, row, index) => {
                return buildTableDateFormatter(theme, value);
            },
        },
        {
            id: "status",
            label: "Status",
            sort: true,
            visible: false,
            formatter: (value, row, index) => {
                return (
                    <FormControl variant="outlined" sx={{ minWidth: 120 }}>
                        <Select
                            value={value ?? ""}
                            onClick={(event) => event.stopPropagation()}
                            onChange={(event) => {
                                const value = event.target.value;
                                handleStatusChange(row, value);
                            }}
                            displayEmpty
                            size="small"
                            inputProps={{
                                "aria-label": "Select Status",
                            }}
                        >
                            {STATUS_LIST.map((item, index) => (
                                <MenuItem
                                    key={`status-${index}`}
                                    value={item.value}
                                >
                                    {item.text}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                );
            },
        },
        {
            id: "member",
            label: "Member",
            sort: true,
            visible: true,
            formatter: (value, row, index) => {
                return (
                    <Switch
                        checked={value === 1 ? true : false}
                        onClick={(event) => event.stopPropagation()}
                        onChange={(event) => {
                            // handle switch change here if needed
                            const value = event.target.checked;
                            handleMemberChange(row, value);
                        }}
                        inputProps={{ "aria-label": "Member Switch" }}
                    />
                );
            },
        },
        {
            id: "optOutEmail",
            label: "Subscribed",
            sort: true,
            //off by default — available from the column picker
            visible: false,
            headerTooltip:
                "Receiving marketing email. Turning this on re-subscribes every patient record sharing this address.",
            formatter: (value, row, index) => {
                return (
                    <Switch
                        checked={value !== true}
                        onClick={(event) => event.stopPropagation()}
                        onChange={(event) => {
                            handleSubscribedChange(row, event.target.checked);
                        }}
                        disabled={
                            !hasAccess(
                                user,
                                permissionConfig,
                                "patient",
                                PERMISSIONS.write,
                            )
                        }
                        inputProps={{ "aria-label": "Subscribed Switch" }}
                    />
                );
            },
        },
        {
            id: "",
            label: "Actions",
            sort: false,
            visible: true,
            formatter: (value, row, index) => {
                const buttons = [];

                if (
                    row.status === STATUS.inactive &&
                    hasAccess(
                        user,
                        permissionConfig,
                        "patient",
                        PERMISSIONS.delete,
                    )
                ) {
                    buttons.push(
                        <IconButton
                            aria-label="delete"
                            onClick={(event) => {
                                event.stopPropagation();

                                handleOnPatientDelete(row);
                            }}
                        >
                            <DeleteIcon />
                        </IconButton>,
                    );
                }

                return buttons;
            },
        },
    ];

    const exportColumn = [
        {
            id: "name",
            label: "Name",
        },
        {
            id: "emailAddress",
            label: "Email",
        },
        {
            id: "age",
            label: "Age",
            formatter: (value, row, index) => {
                if (!value) {
                    return "-";
                }

                return value;
            },
        },
        {
            id: "startingWeight",
            label: `Starting Weight`,
        },
        {
            id: "startingBMI",
            label: `Starting BMI`,
        },
        {
            id: "bodyMass",
            label: `${HEALTH_LABEL["bodyMass"]} (lbs)`,
        },
        {
            id: "bmi",
            label: HEALTH_LABEL["bmi"],
        },
        {
            id: HEALTH_TYPE.BODY_FAT_PERCENTAGE,
            label: HEALTH_LABEL[HEALTH_TYPE.BODY_FAT_PERCENTAGE],
        },
        {
            id: HEALTH_TYPE.LEAN_BODY_MASS,
            label: HEALTH_LABEL[HEALTH_TYPE.LEAN_BODY_MASS],
        },
        {
            id: HEALTH_TYPE.STEP_COUNT,
            label: HEALTH_LABEL[HEALTH_TYPE.STEP_COUNT],
        },
        {
            id: "change",
            label: "Change (lbs)",
        },
        {
            id: "bodyMassDate",
            label: "Last Weigh-In",
            formatter: (value, row, index) => {
                return formatDateTime(value, "MM/DD/YYYY HH:mm:ss", false);
            },
        },
        {
            id: "daysFromFirstData",
            label: "Days",
            formatter: (value, row, index) => {
                if (!value) {
                    return "-";
                }

                return Math.abs(value) - 1;
            },
        },
        {
            id: "platformVersion",
            label: "Platform",
        },
        {
            id: "signInService",
            label: "Sign In Service",
        },
        {
            id: "accountCreatedOn",
            label: "Created On",
            formatter: (value, row, index) => {
                return formatDateTime(value, "MM/DD/YYYY HH:mm:ss", false);
            },
        },
        {
            id: "status",
            label: "Status",
        },
    ];

    const handleOnStatusChange = (event, value) => {
        setStatusToggle(value);
    };

    const handleOnMemberChange = (event, value) => {
        const temp = value.filter((item) => item !== true && item !== false);

        setMemberToggle(temp);
    };

    const handleOnSubscribedChange = (event, value) => {
        //keep at least one option selected, otherwise the list empties with no
        //obvious way back
        if (!value || value.length === 0) {
            return;
        }

        setSubscribedToggle(value);
    };

    const handleOnPlatformChange = (event, value) => {
        setPlatformToggle(value);
    };

    const handleOnFirstDataChange = (event, value) => {
        setFirstData(value);
    };

    const handleAppVersionChange = (event) => {
        const {
            target: { value },
        } = event;
        setSelectedAppVersion(
            typeof value === "string" ? value.split(",") : value,
        );
    };

    //every app version selected filters nothing, so send no version filter —
    //keeps the request short as the version list grows. Request-only; the
    //filter UI and its cache still hold the full selection.
    const prepareExtraParams = (params) => {
        if (
            params.buildVersion &&
            appVersions.length > 0 &&
            appVersions.every((v) => params.buildVersion.includes(v))
        ) {
            return { ...params, buildVersion: [] };
        }

        return params;
    };

    const filterUI = [
        <Box key={`filter-group-member`}>
            <ToggleButtonGroup
                key={`toggle-button-group-member`}
                size="small"
                color="primary"
                value={memberToggle}
                onChange={handleOnMemberChange}
                aria-label="Member"
                title="Toggle Member / Non-member"
                sx={{
                    mr: 3,
                    "& .Mui-selected": {
                        backgroundColor: theme.toggleButtonGroup.selected.main,
                        color: theme.toggleButtonGroup.selected.color,
                        "&:hover": {
                            backgroundColor: theme.toggleButtonGroup.hover.main,
                        },
                    },
                }}
            >
                {MEMBER_LIST.map((item, index) => (
                    <ToggleButton
                        key={`button-group-member${index}`}
                        value={`${item.value}`}
                    >
                        {item.text}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
        </Box>,
        <Box key={`filter-group-subscribed`}>
            <ToggleButtonGroup
                key={`toggle-button-group-subscribed`}
                size="small"
                color="primary"
                value={subscribedToggle}
                onChange={handleOnSubscribedChange}
                aria-label="Subscribed"
                title="Toggle Subscribed / Unsubscribed"
                sx={{
                    mr: 3,
                    "& .Mui-selected": {
                        backgroundColor: theme.toggleButtonGroup.selected.main,
                        color: theme.toggleButtonGroup.selected.color,
                        "&:hover": {
                            backgroundColor: theme.toggleButtonGroup.hover.main,
                        },
                    },
                }}
            >
                {SUBSCRIBED_LIST.map((item, index) => (
                    <ToggleButton
                        key={`button-group-subscribed${index}`}
                        value={`${item.value}`}
                    >
                        {item.text}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
        </Box>,
        <Box key={`filter-group-1`}>
            <ToggleButtonGroup
                key={`toggle-button-group-1`}
                size="small"
                color="primary"
                value={statusToggle}
                onChange={handleOnStatusChange}
                aria-label="Status"
                title="Togle Active / Inactive"
                sx={{
                    mr: 3,
                    "& .Mui-selected": {
                        backgroundColor: theme.toggleButtonGroup.selected.main,
                        color: theme.toggleButtonGroup.selected.color,
                        "&:hover": {
                            backgroundColor: theme.toggleButtonGroup.hover.main,
                        },
                    },
                }}
            >
                {STATUS_LIST.map((item, index) => (
                    <ToggleButton
                        key={`button-group-status${index}`}
                        value={`${item.value}`}
                    >
                        {item.text}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
        </Box>,
        <Box key={`filter-group-2`}>
            <ToggleButtonGroup
                key={`toggle-button-group-2`}
                size="small"
                color="primary"
                value={platformToggle}
                onChange={handleOnPlatformChange}
                aria-label="Platform"
                title="Toggle iOS / Android"
                sx={{
                    mr: 3,
                    "& .Mui-selected": {
                        backgroundColor: theme.toggleButtonGroup.selected.main,
                        color: theme.toggleButtonGroup.selected.color,
                        "&:hover": {
                            backgroundColor: theme.toggleButtonGroup.hover.main,
                        },
                    },
                }}
            >
                {PLATFORM_LIST.map((item, index) => (
                    <ToggleButton
                        key={`platform-button-group-status${index}`}
                        value={`${item.value}`}
                    >
                        {item.text}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
        </Box>,
        <Box key={`filter-group-first-data`}>
            <ToggleButtonGroup
                key={`toggle-button-group-first-data`}
                size="small"
                color="primary"
                value={firstData}
                onChange={handleOnFirstDataChange}
                aria-label="First Data"
                title="Toggle First Data Yes / No"
                sx={{
                    mr: 3,
                    "& .Mui-selected": {
                        backgroundColor: theme.toggleButtonGroup.selected.main,
                        color: theme.toggleButtonGroup.selected.color,
                        "&:hover": {
                            backgroundColor: theme.toggleButtonGroup.hover.main,
                        },
                    },
                }}
            >
                <ToggleButton
                    key={`first-data-button-group-status-00`}
                    value={`First Data?`}
                    disabled
                >
                    First Data?
                </ToggleButton>
                {FIRST_DATA_LIST.map((item, index) => (
                    <ToggleButton
                        key={`first-data-button-group-status${index}`}
                        value={`${item.value}`}
                    >
                        {item.text}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
        </Box>,
        <Box key={`filter-group-3`}>
            <FormControl
                sx={{ m: 1, width: 200 }}
                size="small"
                key={`toggle-button-group-3`}
            >
                <InputLabel id="demo-multiple-checkbox-label">
                    App Version
                </InputLabel>
                <Select
                    labelId="select-app-version"
                    id="select-app-version"
                    multiple
                    value={selectedAppVersion}
                    onChange={handleAppVersionChange}
                    input={<OutlinedInput label="App Version" />}
                    renderValue={(selected) => selected.join(", ")}
                >
                    {appVersions.map((name) => (
                        <MenuItem key={`app-version-${name}`} value={name}>
                            <Checkbox
                                checked={selectedAppVersion.includes(name)}
                            />
                            <ListItemText primary={name} />
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Box>,
    ];

    const actions = [
        {
            id: "export",
            label: "Export",
            callback: async (result) => {},
        },
    ];

    if (hasAccess(user, permissionConfig, "email", PERMISSIONS.read)) {
        actions.push({
            id: "batchEmail",
            label: "Batch Email",
            callback: async (result, checkCache) => {
                if (!checkCache || checkCache.length === 0) {
                    dispatch(
                        showAlert({
                            message: "Please select patients to send email to.",
                        }),
                    );
                    return;
                }

                try {
                    const result = await httpRequest(
                        `email/emailAddress`,
                        { id: { $in: checkCache } },
                        "POST",
                        (error) => {
                            onRequestError(dispatch, navigate, {
                                title: error.title,
                                message: error.message,
                            });
                        },
                    );

                    if (result === false) {
                        return;
                    }

                    const emails = result.payload.map(
                        (patient) => patient.emailAddress,
                    );
                    setInitialRecipient(emails);
                    setOpenEmail(true);
                } catch (error) {
                    console.error("Error fetching email lists:", error);
                }
            },
        });
    }

    if (hasAccess(user, permissionConfig, "patient", PERMISSIONS.write)) {
        actions.push({
            id: "batchUpdateMember",
            label: "Set as Member",
            callback: async (result, checkCache) => {
                if (!checkCache || checkCache.length === 0) {
                    dispatch(
                        showAlert({
                            message: "Please select patients.",
                        }),
                    );
                    return;
                }

                dispatch(
                    showAlert({
                        message: "Set selected patients as members?",
                        title: "Confirm",
                        buttons: [
                            {
                                label: "Yes",
                                callback: async () => {
                                    dispatch(
                                        showLoader({
                                            message:
                                                "Setting selected patients as members...",
                                        }),
                                    );

                                    const patients = [...checkCache].map(
                                        (id) => {
                                            return {
                                                id: id,
                                                fields: { member: 1 },
                                            };
                                        },
                                    );

                                    result.forEach((row) => {
                                        row.member = true;
                                    });

                                    setUpdatedRows(result);

                                    try {
                                        const result = await httpRequest(
                                            `patient/massUpdate`,
                                            { patients: patients },
                                            "POST",
                                            (error) => {
                                                onRequestError(
                                                    dispatch,
                                                    navigate,
                                                    {
                                                        title: error.title,
                                                        message: error.message,
                                                    },
                                                );
                                                dispatch(hideLoader());
                                            },
                                        );

                                        if (result === false) {
                                            return;
                                        }

                                        dispatch(hideLoader());
                                    } catch (error) {
                                        dispatch(hideLoader());
                                        console.error(
                                            "Error fetching email lists:",
                                            error,
                                        );
                                    }
                                },
                            },
                            { label: "No" },
                        ],
                    }),
                );
            },
        });

        actions.push({
            id: "batchUpdateNonMember",
            label: "Set as Non-Member",
            callback: async (result, checkCache) => {
                if (!checkCache || checkCache.length === 0) {
                    dispatch(
                        showAlert({
                            message: "Please select patients.",
                        }),
                    );
                    return;
                }

                dispatch(
                    showAlert({
                        message: "Set selected patients as non-members?",
                        title: "Confirm",
                        buttons: [
                            {
                                label: "Yes",
                                callback: async () => {
                                    dispatch(
                                        showLoader({
                                            message:
                                                "Setting selected patients as non-members...",
                                        }),
                                    );

                                    const patients = [...checkCache].map(
                                        (id) => {
                                            return {
                                                id: id,
                                                fields: { member: 0 },
                                            };
                                        },
                                    );

                                    result.forEach((row) => {
                                        row.member = false;
                                    });

                                    setUpdatedRows(result);

                                    try {
                                        const result = await httpRequest(
                                            `patient/massUpdate`,
                                            { patients: patients },
                                            "POST",
                                            (error) => {
                                                onRequestError(
                                                    dispatch,
                                                    navigate,
                                                    {
                                                        title: error.title,
                                                        message: error.message,
                                                    },
                                                );
                                                dispatch(hideLoader());
                                            },
                                        );

                                        if (result === false) {
                                            return;
                                        }

                                        dispatch(hideLoader());
                                    } catch (error) {
                                        dispatch(hideLoader());
                                        console.error(
                                            "Error fetching email lists:",
                                            error,
                                        );
                                    }
                                },
                            },
                            { label: "No" },
                        ],
                    }),
                );
            },
        });
    }

    if (!user || !ready) {
        return;
    }

    return (
        <Container sx={containerStyle}>
            <HomeAppBar
                theme={theme}
                selectedPage="patients"
                user={user}
                permissionConfig={permissionConfig}
            />

            <Box sx={{ p: { xs: 1, md: 2 } }}>
                <Typography variant="h4">Patients</Typography>

                <Box sx={{ mt: 3 }}>
                    <MuiTable
                        theme={theme}
                        id={"id"}
                        actions={actions}
                        endPoint={"patient/list"}
                        headCells={columns}
                        exportColumns={exportColumn}
                        rowsPerPageArray={[100, 200, 500]}
                        updatedRows={updatedRows}
                        filterUI={filterUI}
                        tempExtraParams={extraParams}
                        prepareExtraParams={prepareExtraParams}
                        CACHE_KEY={LOCAL_STORAGE_KEY.patientsCache}
                        initFilterValues={initFilterValues}
                        stickyHeader={true}
                        stickyHeaderOffset={75}
                    />
                </Box>
            </Box>
            <Email
                theme={theme}
                isOpen={openEmail}
                onClose={() => setOpenEmail(false)}
                initialSubject={""}
                initialContent={""}
                initialRecipient={initialRecipient}
            />
        </Container>
    );
}
